import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { BusinessApiService } from '../services/business-api.service';
import { BusinessResponse } from '../models/business.model';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { DatetimeService } from '../../../shared/services/datetime.service';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';
type BusinessTab = 'info' | 'timeline';

/**
 * Detalle estándar de Business (INC 18 + INC 10): modal con tabs "Información" y
 * "Línea de tiempo". Sigue el patrón canónico de contacto-detail-modal /
 * empresa-detail-modal: la línea de tiempo se delega en el componente compartido
 * `<app-audit-timeline>` (iconos por entidad, estado de error, totalElements,
 * headline legible vía AuditDisplayService) — nada de timeline a mano.
 */
@Component({
  selector: 'app-business-detail-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, BadgeComponent, AuditTimelineComponent],
  templateUrl: './business-detail-modal.component.html'
})
export class BusinessDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() business: BusinessResponse | null = null;
  @Input() detailLoading = false;
  @Output() close = new EventEmitter<void>();
  @Output() edit = new EventEmitter<BusinessResponse>();

  activeTab: BusinessTab = 'info';

  timelineLogs: AuditLogResponse[] = [];
  timelineLoading = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;
  /** Evita recargar el timeline en cada ida y vuelta al tab cuando no hay eventos. */
  timelineLoaded = false;

  readonly tabs: { id: BusinessTab; label: string }[] = [
    { id: 'info', label: 'Información' },
    { id: 'timeline', label: 'Línea de tiempo' },
  ];

  constructor(
    private businessApi: BusinessApiService,
    private datetimeService: DatetimeService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen && this.business) {
      this.activeTab = 'info';
      this.timelineLogs = [];
      this.timelineError = '';
      this.timelinePage = 0;
      this.timelineTotalPages = 0;
      this.timelineTotalElements = 0;
      this.timelineLoaded = false;
    }
  }

  setTab(tab: BusinessTab): void {
    this.activeTab = tab;
    if (tab === 'timeline' && !this.timelineLoaded && !this.timelineLoading && this.business) {
      this.cargarTimeline(0);
    }
  }

  cargarTimeline(page: number): void {
    if (!this.business?.id) return;
    this.timelineLoading = true;
    this.timelineError = '';
    this.timelinePage = page;
    this.businessApi.getTimeline(this.business.id, page, 15).subscribe({
      next: (res) => {
        this.timelineLogs = res.content ?? [];
        this.timelinePage = res.page ?? page;
        this.timelineTotalPages = res.totalPages ?? 0;
        this.timelineTotalElements = res.totalElements ?? 0;
        this.timelineLoading = false;
        this.timelineLoaded = true;
      },
      error: () => {
        this.timelineLogs = [];
        this.timelineError = 'Error al cargar la línea de tiempo.';
        this.timelineLoading = false;
        // No se marca como cargada: un reintento al volver al tab es deseable.
      }
    });
  }

  formatDate(dateString?: string | null): string {
    return this.datetimeService.formatDateTime(dateString);
  }

  getEstadoLabel(): string { return (this.business?.isActive ?? false) ? 'Activo' : 'Inactivo'; }
  getEstadoColor(): BadgeColor { return (this.business?.isActive ?? false) ? 'success' : 'error'; }

  onClose(): void { this.close.emit(); }

  onEdit(): void {
    if (this.business) this.edit.emit(this.business);
  }
}
